"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b ||= {})
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import classNames from "classnames";
import { setElementVars } from "@vanilla-extract/dynamic";
import { darkTheme, duration, lightTheme } from "../../../tokens/tokens-css";
import { forceReflow, randomString } from "../../../utils";
import Tooltip, { TooltipPosition, TooltipSize } from "../Tooltip/Tooltip";
import { Expandable } from "../../behaviors/Expandable";
import { IconSize, Warning } from "../../../global/icons";
import { Text } from "../Text/Text";
import { Label } from "../Label";
import { formatTelephone } from "../../../utils/formatString";
import * as styles from "./TextInput-css";
const componentName = "text-input";
export const TextInput = forwardRef(
  (_a, ref) => {
    var _b = _a, {
      type = "text",
      multiline,
      label,
      placeholder = "",
      value,
      variant = "bordered",
      className,
      labelHidden,
      size = "m",
      theme,
      error,
      errorVariant = "default",
      disabled,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onClick,
      maxLength,
      startElement,
      endElement,
      readOnly,
      role,
      onChange,
      id,
      elementSize,
      onFocus,
      onBlur,
      autoGrow
    } = _b, props = __objRest(_b, [
      "type",
      "multiline",
      "label",
      "placeholder",
      "value",
      "variant",
      "className",
      "labelHidden",
      "size",
      "theme",
      "error",
      "errorVariant",
      "disabled",
      "onMouseEnter",
      "onMouseLeave",
      "onKeyDown",
      "onClick",
      "maxLength",
      "startElement",
      "endElement",
      "readOnly",
      "role",
      "onChange",
      "id",
      "elementSize",
      "onFocus",
      "onBlur",
      "autoGrow"
    ]);
    const [focused, setFocused] = useState(false);
    const autoId = useRef(`text-input-${randomString()}`);
    const baseId = id || autoId.current;
    const inputId = `${baseId}-input`;
    const labelId = `${baseId}-label`;
    const errorId = `${baseId}-error`;
    const elementType = multiline ? "textarea" : "input";
    const InputElement = elementType;
    const isUnderlined = variant === "underlined";
    const measureValue = value || placeholder;
    const startElementRef = useRef(null);
    const endElementRef = useRef(null);
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const handleChange = (event) => {
      if (type === "tel") {
        event.target.value = formatTelephone(event.target.value);
      }
      onChange == null ? void 0 : onChange(event.target.value);
    };
    const handleFocus = (event) => {
      setFocused(true);
      onFocus == null ? void 0 : onFocus(event);
    };
    const handleBlur = (event) => {
      setFocused(false);
      onBlur == null ? void 0 : onBlur(event);
    };
    useEffect(() => {
      if (!containerRef.current)
        return;
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === startElementRef.current) {
            setElementVars(containerRef.current, {
              [styles.startElementWidth]: `${startElementRef.current.offsetWidth}px`
            });
          } else if (entry.target === endElementRef.current) {
            setElementVars(containerRef.current, {
              [styles.endElementWidth]: `${endElementRef.current.offsetWidth}px`
            });
          } else if (entry.target === measureRef.current) {
            setElementVars(containerRef.current, __spreadValues({
              [styles.inputWidth]: `${Math.max(
                Math.ceil(entry.target.getBoundingClientRect().width),
                measureRef.current.offsetWidth
              )}px`
            }, multiline ? { [styles.textAreaHeight]: `${measureRef.current.offsetHeight}px` } : {}));
          }
        });
      });
      if (startElement) {
        resizeObserver.observe(startElementRef.current);
      }
      if (endElement) {
        resizeObserver.observe(endElementRef.current);
      }
      if (autoGrow) {
        resizeObserver.observe(measureRef.current);
      }
      return () => resizeObserver.disconnect();
    }, [autoGrow, endElement, multiline, startElement]);
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classNames(componentName, styles.container, className, {
          [lightTheme]: theme === "light",
          [darkTheme]: theme === "dark"
        }),
        ref: containerRef
      },
      /* @__PURE__ */ React.createElement(
        Label,
        {
          long: true,
          hidden: labelHidden || isUnderlined,
          disabled,
          size: size === "s" ? "s" : "m",
          className: classNames(styles.label, `${componentName}__label`),
          id: labelId,
          htmlFor: inputId
        },
        label
      ),
      /* @__PURE__ */ React.createElement(TextInputError, { error, errorVariant, errorId }, /* @__PURE__ */ React.createElement("div", { className: classNames(styles.inputWrapper({ size, variant }), `${componentName}__input-container`) }, autoGrow && /* @__PURE__ */ React.createElement("div", { className: styles.measurer({ variant, multiline }), ref: measureRef }, multiline ? measureValue == null ? void 0 : measureValue.replace(/\n/g, "\n\u200B") : measureValue), /* @__PURE__ */ React.createElement(
        InputElement,
        __spreadValues({
          className: classNames(styles.input({ variant }), `${componentName}__${elementType}`),
          type: multiline ? void 0 : type,
          ref,
          id: inputId,
          "aria-labelledby": labelId,
          "aria-describedby": error ? errorId : void 0,
          "aria-multiline": multiline,
          "aria-invalid": !!error,
          placeholder,
          onChange: handleChange,
          value,
          onFocus: handleFocus,
          onBlur: handleBlur,
          disabled,
          onMouseEnter,
          onMouseLeave,
          onKeyDown,
          onClick,
          maxLength,
          readOnly,
          role,
          size: elementSize
        }, props)
      ), isUnderlined && /* @__PURE__ */ React.createElement(Transition, { onEnter: forceReflow, in: focused, timeout: duration.m }, (status) => /* @__PURE__ */ React.createElement("div", { className: styles.underline({ focused: ["entering", "entered"].includes(status) }) })), !!startElement && /* @__PURE__ */ React.createElement("div", { ref: startElementRef, className: classNames(styles.element({ position: "start" })) }, startElement), !!endElement && /* @__PURE__ */ React.createElement("div", { ref: endElementRef, className: classNames(styles.element({ position: "end" })) }, endElement)))
    );
  }
);
function TextInputError({ children, error, errorVariant, errorId }) {
  switch (errorVariant) {
    case "tooltip": {
      return /* @__PURE__ */ React.createElement(
        Tooltip,
        {
          content: /* @__PURE__ */ React.createElement("div", { id: errorId }, error),
          size: TooltipSize.Large,
          show: !!error,
          position: TooltipPosition.Bottom,
          autoStack: true,
          isError: true
        },
        children
      );
    }
    default:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, children, /* @__PURE__ */ React.createElement(Expandable, { expanded: !!error, timeout: duration.s }, ({ expandableElementRef }) => /* @__PURE__ */ React.createElement("div", { id: errorId, className: styles.error, ref: expandableElementRef, "aria-live": "polite" }, /* @__PURE__ */ React.createElement("div", { className: styles.errorIcon }, /* @__PURE__ */ React.createElement(Warning, { size: IconSize.Small, color: "critical" })), /* @__PURE__ */ React.createElement(Text, { lineHeight: "compact", size: "s" }, error))));
  }
}
